<script lang="ts" generics="T extends UserListRecord">
  import { page } from '$app/state';
  import { goto } from '$app/navigation';
  import { addUser, adSearch } from '$lib/api/users';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faPlus } from '@itdev-fortawesome/pro-solid-svg-icons/faPlus';
  import InfiniteScroll from '$lib/components/common/InfiniteScroll.svelte';
  import { getContext, tick } from 'svelte';
  import QuickSearch from '$lib/components/common/QuickSearch.svelte';
  import SortButton from '$lib/components/common/SortButton.svelte';
  import { type Role, type SortOrder, type User, type UserListRecord, ValidationError } from '$lib/api/types';
  import type { DrawerNavigationContext, TableHeaderItem } from '$lib/componentTypes';
  import { formatDate } from '$lib/util';
  import TableError from '../TableError.svelte';
  import TableMessage from '../TableMessage.svelte';
  import ImpersonateBtn from '../common/ImpersonateBtn.svelte';
  import { faBars } from '@itdev-fortawesome/pro-regular-svg-icons/faBars';
  interface Props {
    route: 'students' | 'instructors' | 'admins';
    role: Role;
    loadData: (query: string, page: number, pageSize: number, signal: AbortSignal) => Promise<T[]>;
    buildNewUserObject: (user: User) => T;
    additionalHeaders: TableHeaderItem<T>[];
    headerText: string;
  }

  let { route, role, loadData, buildNewUserObject, additionalHeaders, headerText }: Props = $props();

  let searchParam = $state(page.url.searchParams.get('search') ?? '');
  let currentSort = $state(page.url.searchParams.get('sortby') ?? 'lastName');
  let currentSortOrder: 'asc' | 'desc' = $state((page.url.searchParams.get('order') as SortOrder | null) ?? 'asc');
  let searchTextBox = $state(page.url.searchParams.get('search') ?? '');
  let queryString = $derived.by(() => {
    const searchParams = page.url.searchParams.toString();
    if (!searchParams || searchParams.length === 0) return 'search=&sortby=lastName&order=asc';
    return page.url.searchParams.toString();
  });

  let showAdUserSearch = $state(false);
  let quickSearch: QuickSearch | null = $state(null);
  let infiniteScroll: InfiniteScroll<T>;
  let newUserUnivId: string | null = $state(null); // universal id of new user if one is selected

  let drawerNavigation = getContext<DrawerNavigationContext>('users-drawer');

  function updateSearch(searchStr: string) {
    searchParam = searchStr.toLowerCase();
  }

  $effect(() => {
    goto(`/admin/users/${route}?search=${searchParam ?? ''}&sortby=${currentSort}&order=${currentSortOrder}`, {
      replaceState: true,
      keepFocus: true,
      noScroll: true,
    });
  });

  async function initiateAdUserSearch() {
    showAdUserSearch = true;
    await tick();
    quickSearch!.focus();
  }

  async function addNewUserSubmit(e: SubmitEvent) {
    e.preventDefault();
    if (!newUserUnivId) {
      return;
    }
    const univIdOfUserToAdd = newUserUnivId;
    currentSort = 'created';
    currentSortOrder = 'desc';
    quickSearch!.clearSelection();
    try {
      const user = await addUser(univIdOfUserToAdd, role);
      infiniteScroll.prependData(buildNewUserObject(user));
    } catch (e) {
      if (e instanceof ValidationError && e.errors['UniversalId'].includes('User already exists')) {
        alert('The user you are trying to add has already been added into the application.');
      } else {
        // all other validation errors should be reported to logs
        throw e;
      }
    }
  }
</script>

<div class="d-flex mb-3 align-items-center gap-1">
  <button
    type="button"
    class="btn drawer-toggle d-md-none"
    onclick={drawerNavigation.openDrawer}
    aria-controls="usersDrawer"
    aria-label="Open drawer navigation"
  >
    <FontAwesomeIcon icon={faBars} class="mt-1" />
  </button>
  <h2 class="mb-0">{headerText}</h2>
</div>
<div>
  <label for="search" class="visually-hidden">Search users</label>
  <input
    class="form-control mb-3"
    bind:value={searchTextBox}
    autocomplete="off"
    id="search"
    type="search"
    placeholder="Search users"
    oninput={e => updateSearch(e.currentTarget.value)}
  />
</div>

<InfiniteScroll bind:this={infiniteScroll} {loadData} {queryString} minimumQueryStringLength={0}>
  {#snippet dataRenderer(users, query, shouldShowLoadingIndicator, error, loadingIndicator)}
    <table class="table table-borderless mt-3">
      <thead>
        <tr class="border-bottom">
          <th scope="col" class="mdvp-hidden"></th>
          <!-- icon btn header -->
          <th scope="col">
            Last Name
            <SortButton sortColumnLabel="last name" bind:currentSortOrder bind:currentSort sortColumn="lastName" />
          </th>
          <th scope="col">
            First Name
            <SortButton sortColumnLabel="first name" bind:currentSortOrder bind:currentSort sortColumn="firstName" />
          </th>
          {#each additionalHeaders.map((h, i) => {
            return { header: h, i };
          }) as h (h.i)}
            <th scope="col" class="text-{h.header.alignment} mdvp-hidden">
              {h.header.title}
              {#if h.header.sortable}
                <SortButton
                  sortColumn={h.header.sortColumnName}
                  sortColumnLabel={h.header.title}
                  bind:currentSortOrder
                  bind:currentSort
                />
              {/if}
            </th>
          {/each}
          <th scope="col" class="text-end mdvp-hidden">
            Date Added
            <SortButton sortColumnLabel="date added" bind:currentSortOrder bind:currentSort sortColumn="created" />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td colspan={route === 'students' ? 5 : 4}>
            {#if showAdUserSearch}
              <form class="d-flex gap-3" onsubmit={addNewUserSubmit}>
                <div class="flex-grow-1">
                  <label for="addUserActiveDirQuickSearch" class="visually-hidden">
                    Select {role} from Active Directory
                  </label>
                  <QuickSearch
                    minimumQueryLength={2}
                    id="addUserActiveDirQuickSearch"
                    placeholder="Search Active Directory"
                    bind:this={quickSearch}
                    bind:value={newUserUnivId}
                    loadData={async (query, page, pageSize, signal) => {
                      return (await adSearch(query, route === 'students', page, pageSize, signal)).map(user => {
                        return {
                          value: user.universalId,
                          text: `${user.firstName} ${user.lastName}`,
                          title: `${user.firstName} ${user.lastName}`,
                          subtitle:
                            route === 'students' ? `Student ID: ${user.studentId}` : `Employee ID: ${user.employeeId}`,
                        };
                      });
                    }}
                    emptySearchResultsLabel="No {route === 'students' ? 'students' : 'users'} found"
                    beginSearchingLabel="Enter first name, last name, domain name, or {route === 'students'
                      ? 'student'
                      : 'employee'} ID to begin searching Active Directory."
                  />
                </div>
                <input type="hidden" name="newUserUniversalId" bind:value={newUserUnivId} />
                <button type="submit" class="btn btn-primary" disabled={!newUserUnivId}>Add {role}</button>
                <button type="button" class="btn btn-secondary" onclick={() => (showAdUserSearch = false)}
                  >Cancel</button
                >
              </form>
            {:else}
              <button class="btn btn-link p-0" onclick={initiateAdUserSearch}>
                <FontAwesomeIcon icon={faPlus} class="me-2" />Click to add {role}
              </button>
            {/if}
          </td>
        </tr>
        {#each users as user (user.id)}
          <tr onclick={() => goto(`/admin/users/${route}/${user.id}`)}>
            <td class="mdvp-hidden">
              <ImpersonateBtn userId={user.id} />
            </td>
            <td><a href="/admin/users/{route}/{user.id}">{user.lastName}</a></td>
            <td><a href="/admin/users/{route}/{user.id}">{user.firstName}</a></td>
            {#each additionalHeaders.map((h, i) => {
              return { header: h, i };
            }) as h (h.i)}
              <td class="text-{h.header.alignment} mdvp-hidden">
                {h.header.selectValue(user)}
              </td>
            {/each}
            <td class="text-end mdvp-hidden">{formatDate(user.created)}</td>
          </tr>
        {/each}
      </tbody>
    </table>

    {#if error}
      <TableError {error} />
    {:else if shouldShowLoadingIndicator}
      {@render loadingIndicator()}
    {:else if users.length === 0}
      <TableMessage message="No users found!" />
    {/if}
  {/snippet}
  {#snippet loadingIndicator()}
    <TableMessage message="Loading..." />
  {/snippet}
</InfiniteScroll>

<style>
  .drawer-toggle {
    --bs-btn-font-size: 20px;
    --bs-btn-padding-x: 0.25rem;
    --bs-btn-padding-y: 0.25rem;
    --bs-btn-color: black;
  }

  .table > tbody > tr:not(:first-child) > * {
    cursor: pointer;
  }

  .table > tbody > tr:not(:first-child):hover > * {
    --bs-table-color-state: var(--bs-table-hover-color);
    --bs-table-bg-state: var(--bs-table-hover-bg);
  }
</style>
